import React, { useState } from 'react';
import { MessageCircle, Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { siteContent } from '@/data/siteContent';
import { CONTACT_INFO, FAQS } from '../data/mockData';

const { global } = siteContent;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleWhatsAppClick = (templateName = 'generic', variables = {}) => {
    trackWhatsAppClick({ templateName, page: 'contact' });
    const link = generateWhatsAppLink(templateName, variables);
    window.open(link, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());

      const response = await apiService.submitContactForm(data);
      toast.success('Message sent! We\'ll respond within 24 hours. Reference: ' + response.submission_id);
      e.target.reset();
    } catch (error) {

      toast.error('Failed to send message. Please try WhatsApp or email instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Fastest response',
      value: global.phone,
      action: () => handleWhatsAppClick('generic', {}),
      color: 'text-[#25D366]',
      bg: 'bg-[#25D366]/10'
    },
    {
      icon: Phone,
      title: 'Call Us',
      description: 'Direct phone support',
      value: CONTACT_INFO.phone,
      link: `tel:${CONTACT_INFO.phone}`,
      color: 'text-[#039fff]',
      bg: 'bg-[#039fff]/10'
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'For detailed inquiries',
      value: CONTACT_INFO.email,
      link: `mailto:${CONTACT_INFO.email}`,
      color: 'text-[#fb6402]',
      bg: 'bg-[#fb6402]/10'
    }
  ];

  const officeInfo = [
    { icon: MapPin, label: 'Service Area', value: 'Stellenbosch & Greater Cape Town' },
    { icon: Clock, label: 'Operating Hours', value: CONTACT_INFO.operatingHours }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-300 mb-8">
              We're here to help with your delivery and moving needs
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <a key={index} href={method.link} target={method.title === 'WhatsApp' ? '_blank' : undefined} rel={method.title === 'WhatsApp' ? 'noopener noreferrer' : undefined}>
                  <Card className="hover:shadow-xl transition-all h-full cursor-pointer">
                    <CardContent className="p-8 text-center">
                      <div className={`${method.bg} rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4`}>
                        <Icon className={`h-8 w-8 ${method.color}`} />
                      </div>
                      <h3 className="font-bold text-[#001A47] mb-2">{method.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{method.description}</p>
                      <p className="font-semibold text-[#001A47]">{method.value}</p>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#001A47] mb-6">Send Us a Message</h2>
              <Card>
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Your Name</Label>
                        <Input id="name" name="name" placeholder="Full name" required />
                      </div>
                      <div>
                        <Label htmlFor="mobile">Mobile Number</Label>
                        <Input id="mobile" name="mobile" type="tel" placeholder="+27" required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input id="email" name="email" type="email" placeholder="your@email.com" required />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <select id="subject" name="subject" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" required>
                        <option value="">Select a topic...</option>
                        <option>General Inquiry</option>
                        <option>Get a Quote</option>
                        <option>Business Account</option>
                        <option>Track My Order</option>
                        <option>Report an Issue</option>
                        <option>Partnership Inquiry</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea id="message" name="message" placeholder="Tell us how we can help..." rows={5} required />
                    </div>
                    
                    {/* Marketing Opt-in (POPIA Compliant) */}
                    <div className="pt-4 border-t space-y-3">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="marketing_opt_in"
                          name="marketing_opt_in"
                          className="mt-1 rounded border-gray-300"
                        />
                        <Label htmlFor="marketing_opt_in" className="cursor-pointer text-sm text-gray-600">
                          I agree to receive promotional offers and updates via WhatsApp and email. You can opt out anytime.
                        </Label>
                      </div>
                      <p className="text-xs text-gray-500">
                        By submitting, you agree to our <a href="/terms" className="text-[#039fff] hover:underline">Terms & Conditions</a> and <a href="/privacy" className="text-[#039fff] hover:underline">Privacy Policy (POPIA)</a>.
                      </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button type="submit" disabled={isSubmitting} className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                        <Send className="mr-2 h-4 w-4" />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleWhatsAppClick('generic', {})}
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        WhatsApp Instead
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Office Info & Map */}
            <div>
              <h2 className="text-3xl font-bold text-[#001A47] mb-6">Our Service Area</h2>
              
              {/* Office Details */}
              <div className="space-y-4 mb-8">
                {officeInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <Card key={index}>
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="bg-[#039fff]/10 rounded-full p-3 flex-shrink-0">
                            <Icon className="h-6 w-6 text-[#039fff]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#001A47] mb-1">{info.label}</p>
                            <p className="text-gray-600 text-sm">{info.value}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Map Placeholder */}
              <Card>
                <CardContent className="p-0">
                  <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="h-12 w-12 text-[#039fff] mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">Interactive Map</p>
                      <p className="text-sm text-gray-500">Google Maps integration coming soon</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-6 bg-[#039fff]/5 p-6 rounded-lg">
                <h3 className="font-bold text-[#001A47] mb-3">Primary Service Areas</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                  <div>• Stellenbosch</div>
                  <div>• Somerset West</div>
                  <div>• Cape Town CBD</div>
                  <div>• Northern Suburbs</div>
                  <div>• Southern Suburbs</div>
                  <div>• Paarl & Winelands</div>
                </div>
                <a href="/areas" className="inline-block mt-4">
                  <Button variant="link" className="p-0 h-auto text-[#039fff]">
                    View all areas we serve →
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Access */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-8">Quick Answers</h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.slice(0, 5).map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-[#001A47] hover:text-[#039fff]">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="text-center mt-8">
              <a href="/#faq">
                <Button variant="outline">View All FAQs</Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
